import { useEffect, useRef, useState } from 'react'
import { Badge, Button, Dropdown, Empty, Input, List, Popover, Spin, Typography } from 'antd'
import type { MenuProps, InputRef } from 'antd'
import {
  ArrowRightOutlined,
  MenuOutlined,
  LogoutOutlined,
  UserOutlined,
  SearchOutlined,
  BellOutlined,
  CheckOutlined,
  TeamOutlined,
  FileTextOutlined,
  CommentOutlined,
  CloudServerOutlined,
  AlertOutlined,
  CheckCircleOutlined,
  FileProtectOutlined,
  SmileOutlined,
  SyncOutlined,
  WarningOutlined,
  ShoppingCartOutlined,
  UserAddOutlined,
  CreditCardOutlined,
  StopOutlined,
  FileSyncOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import apiClient from '../api/client'
import { useAuthStore } from '../stores/auth'
import LanguageSwitcher from './LanguageSwitcher'

// Sticky top header with the burger toggle (mobile only) on the left
// and the user menu on the right. Page title comes from props so the
// AppLayout can derive it from the route.

interface AdminHeaderProps {
  pageTitle?: string
  onMenuToggle: () => void
}

export default function AdminHeader({ pageTitle, onMenuToggle }: AdminHeaderProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const setUser = useAuthStore((s) => s.setUser)

  const handleLogout = async () => {
    await apiClient.post('/api/v1/auth/logout').catch(() => undefined)
    setUser(null)
    navigate('/login')
  }

  const userMenu: MenuProps['items'] = [
    {
      key: 'account',
      icon: <UserOutlined />,
      label: t('account.title', 'Mein Konto'),
      onClick: () => navigate('/account'),
    },
    { type: 'divider' },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('auth.logout', 'Abmelden'),
      onClick: handleLogout,
    },
  ]

  return (
    <header className="header">
      <div className="header-left">
        <Button
          className="header-menu-toggle"
          type="text"
          icon={<MenuOutlined />}
          onClick={onMenuToggle}
          aria-label="Menü öffnen"
        />
        {pageTitle && <Typography.Text className="header-title">{pageTitle}</Typography.Text>}
      </div>
      <div className="header-search">
        <GlobalCustomerSearch />
      </div>
      <div className="header-actions">
        <LanguageSwitcher />
        <NotificationBell />
        <Dropdown menu={{ items: userMenu }} placement="bottomRight" trigger={['click']}>
          <button className="header-avatar-btn" aria-label="Benutzer-Menü">
            <span className="header-avatar">
              {(user?.actor || 'A').charAt(0).toUpperCase()}
            </span>
            <span className="header-user">{user?.actor || ''}</span>
          </button>
        </Dropdown>
      </div>
    </header>
  )
}

// ── NotificationBell ──────────────────────────────────────────────────

interface Notif {
  id: number
  type: string
  message: string
  link?: string | null
  read_at?: string | null
  created_at: string
}

function NotificationBell() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)

  const countQ = useQuery({
    queryKey: ['notif-count'],
    queryFn: async () => {
      const r = await apiClient.get<{ data: { unread: number } }>('/api/v1/notifications/count')
      return r.data.data.unread
    },
    refetchInterval: 30_000,
    staleTime: 20_000,
  })

  const listQ = useQuery({
    queryKey: ['notif-list'],
    queryFn: async () => {
      const r = await apiClient.get<{ data: { items: Notif[] } }>('/api/v1/notifications?limit=30')
      return r.data.data.items
    },
    enabled: open,
    staleTime: 10_000,
  })

  const markAll = useMutation({
    mutationFn: () => apiClient.patch('/api/v1/notifications/mark-all-read'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notif-count'] })
      qc.invalidateQueries({ queryKey: ['notif-list'] })
    },
  })

  const markOne = useMutation({
    mutationFn: (id: number) => apiClient.patch(`/api/v1/notifications/${id}/read`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notif-count'] })
      qc.invalidateQueries({ queryKey: ['notif-list'] })
    },
  })

  const handleClick = (n: Notif) => {
    if (!n.read_at) markOne.mutate(n.id)
    if (n.link) {
      setOpen(false)
      navigate(n.link)
    }
  }

  const fmtTime = (iso: string) => {
    const d = new Date(iso)
    const now = new Date()
    const diffMin = Math.floor((now.getTime() - d.getTime()) / 60000)
    if (diffMin < 1) return t('notif.justNow', 'gerade eben')
    if (diffMin < 60) return t('notif.minsAgo', '{{m}} Min.', { m: diffMin })
    const diffH = Math.floor(diffMin / 60)
    if (diffH < 24) return t('notif.hoursAgo', '{{h}} Std.', { h: diffH })
    return d.toLocaleDateString()
  }

  const unread = countQ.data ?? 0
  const items = listQ.data ?? []

  const notifMeta: Record<string, { icon: React.ReactNode; color: string }> = {
    sla_breach:              { icon: <AlertOutlined />,        color: '#ff4d4f' },
    monitoring_down:         { icon: <WarningOutlined />,      color: '#fa8c16' },
    monitoring_recovery:     { icon: <CheckCircleOutlined />,  color: '#52c41a' },
    drift_alert:             { icon: <SyncOutlined />,         color: '#fa8c16' },
    quote_accepted:          { icon: <FileProtectOutlined />,  color: '#0EA5E9' },
    quote_converted:         { icon: <FileTextOutlined />,     color: '#0EA5E9' },
    csat_response:           { icon: <SmileOutlined />,        color: '#722ed1' },
    ticket_opened:           { icon: <CommentOutlined />,      color: '#0ea5e9' },
    ticket_replied:          { icon: <CommentOutlined />,      color: '#06b6d4' },
    payment_received:        { icon: <CreditCardOutlined />,   color: '#10b981' },
    subscription_cancelled:  { icon: <StopOutlined />,         color: '#ef4444' },
    order_placed:            { icon: <ShoppingCartOutlined />, color: '#f59e0b' },
    portal_signup:           { icon: <UserAddOutlined />,      color: '#8b5cf6' },
  }
  const defaultMeta = { icon: <BellOutlined />, color: '#8c8c8c' }

  const visibleItems = items.slice(0, 10)

  const content = (
    <div className="notif-popover">
      <div className="notif-header">
        <Typography.Text strong>{t('notif.title', 'Benachrichtigungen')}</Typography.Text>
        {unread > 0 && (
          <Button size="small" type="link" icon={<CheckOutlined />}
            loading={markAll.isPending}
            onClick={() => markAll.mutate()}>
            {t('notif.markAllRead', 'Alle gelesen')}
          </Button>
        )}
      </div>
      {listQ.isLoading ? (
        <div className="notif-loading"><Spin size="small" /></div>
      ) : items.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('notif.empty', 'Keine Benachrichtigungen')}
          style={{ padding: '24px 0' }} />
      ) : (
        <>
          <List
            size="small"
            dataSource={visibleItems}
            className="notif-list"
            renderItem={(n) => {
              const meta = notifMeta[n.type] ?? defaultMeta
              return (
                <List.Item
                  key={n.id}
                  className={`notif-item${n.read_at ? '' : ' notif-item--unread'}`}
                  style={{ cursor: n.link ? 'pointer' : 'default' }}
                  onClick={() => handleClick(n)}
                >
                  <div className="notif-item-inner">
                    <span className="notif-item-icon" style={{ color: meta.color }}>
                      {meta.icon}
                    </span>
                    <div className="notif-item-content">
                      <div className="notif-item-row">
                        <Typography.Text className="notif-item-message">{n.message}</Typography.Text>
                        {!n.read_at && <span className="notif-unread-dot" />}
                      </div>
                      <span className="notif-item-time">{fmtTime(n.created_at)}</span>
                    </div>
                  </div>
                </List.Item>
              )
            }}
          />
          <div className="notif-footer">
            <Button
              type="link"
              size="small"
              icon={<ArrowRightOutlined />}
              iconPosition="end"
              onClick={() => { setOpen(false); navigate('/admin-notifications') }}
            >
              {t('notif.viewAll', 'Alle Benachrichtigungen')}
              {items.length > 10 ? ` (${items.length})` : ''}
            </Button>
          </div>
        </>
      )}
    </div>
  )

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
      arrow={false}
      overlayInnerStyle={{ padding: 0, borderRadius: 8, overflow: 'hidden' }}
    >
      <Badge count={unread} overflowCount={99} size="small" offset={[-2, 2]}>
        <Button type="text" icon={<BellOutlined />} aria-label={t('notif.bell', 'Benachrichtigungen')} />
      </Badge>
    </Popover>
  )
}

// ── GlobalSearch ──────────────────────────────────────────────────────
//
// Unified search across customers, invoices, tickets and subscriptions.
// 250ms debounce; dropdown groups results by entity kind.

interface SearchHit {
  kind: 'customer' | 'invoice' | 'ticket' | 'subscription' | 'order' | 'quote'
  id: number
  title: string
  sub: string
  url: string
}

const KIND_ICON: Record<SearchHit['kind'], React.ReactNode> = {
  customer:     <TeamOutlined />,
  invoice:      <FileTextOutlined />,
  ticket:       <CommentOutlined />,
  subscription: <CloudServerOutlined />,
  order:        <ShoppingCartOutlined />,
  quote:        <FileSyncOutlined />,
}

const KIND_LABEL: Record<SearchHit['kind'], string> = {
  customer:     'Kunden',
  invoice:      'Rechnungen',
  ticket:       'Tickets',
  subscription: 'Abonnements',
  order:        'Bestellungen',
  quote:        'Angebote',
}

function GlobalCustomerSearch() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const [debounced, setDebounced] = useState('')
  const wrapRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<InputRef>(null)

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(q), 250)
    return () => window.clearTimeout(id)
  }, [q])

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        inputRef.current?.focus()
        setOpen(true)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  const resQ = useQuery({
    queryKey: ['global-search', debounced],
    enabled: debounced.length >= 2,
    queryFn: async () => {
      const res = await apiClient.get<{ data: { hits: SearchHit[] } }>(
        `/api/v1/search?q=${encodeURIComponent(debounced)}`)
      return res.data.data.hits ?? []
    },
    staleTime: 15_000,
  })

  const pick = (h: SearchHit) => {
    setOpen(false)
    setQ('')
    navigate(h.url)
  }

  // Group by kind preserving order
  const grouped = (resQ.data ?? []).reduce<Record<string, SearchHit[]>>((acc, h) => {
    if (!acc[h.kind]) acc[h.kind] = []
    acc[h.kind].push(h)
    return acc
  }, {})
  const kinds = Object.keys(grouped) as SearchHit['kind'][]

  return (
    <div ref={wrapRef} className="global-search-wrap">
      <Input
        ref={inputRef}
        prefix={<SearchOutlined />}
        placeholder={t('header.search', 'Kunden, Rechnungen, Tickets…')}
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => { if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur() } }}
        suffix={q ? undefined : <kbd className="search-kbd">⌘K</kbd>}
        allowClear
        className="global-search-input"
      />
      {open && debounced.length >= 2 && (
        <div className="global-search-dropdown">
          {resQ.isLoading ? (
            <div className="global-search-empty"><Spin size="small" /></div>
          ) : kinds.length === 0 ? (
            <div className="global-search-empty">
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t('header.searchNoResults', 'Keine Treffer')} />
            </div>
          ) : (
            <ul className="global-search-list">
              {kinds.map((kind) => (
                <>
                  <li key={`head-${kind}`} className="global-search-group-header">
                    {KIND_ICON[kind]} {KIND_LABEL[kind] ?? kind}
                  </li>
                  {grouped[kind].map((h) => (
                    <li key={`${h.kind}-${h.id}`} className="global-search-item"
                      onMouseDown={(e) => { e.preventDefault(); pick(h) }}>
                      <Typography.Text strong>{h.title}</Typography.Text>
                      <span className="global-search-meta">{h.sub}</span>
                    </li>
                  ))}
                </>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
